import type { VersionMeta, VersionCheckConfig } from '../types/version.types';
import {
  setVersionOutdated,
  getVersionOutdated,
  notifyVersionOutdated,
  stopAllPeriodicTasks,
} from '../utils/versionOutdated';

const DEFAULT_CHECK_INTERVAL = 7 * 60 * 1000; // 7 минут

class VersionCheckService {
  private checkTimer: number | null = null;
  private config: VersionCheckConfig = {};
  private currentVersion: string | null = null;

  /**
   * Получить текущую версию приложения
   */
  getCurrentVersion(): string {
    if (this.currentVersion) {
      return this.currentVersion;
    }

    // Попробуем получить из import.meta.env
    try {
      const version = import.meta.env.VITE_APP_VERSION as string | undefined;
      if (version) {
        this.currentVersion = version;
        return version;
      }
    } catch (error) {
      console.warn('[versionCheck] Не удалось получить версию из import.meta.env', error);
    }

    // Попробуем получить из мета-тегов
    try {
      const metaVersion = document.querySelector('meta[name="app:version"]');
      if (metaVersion && metaVersion.getAttribute('content')) {
        const version = metaVersion.getAttribute('content')!;
        this.currentVersion = version;
        return version;
      }
    } catch (error) {
      console.warn('[versionCheck] Не удалось получить версию из мета-тегов', error);
    }

    // Если не удалось получить версию, вернем 'unknown'
    this.currentVersion = 'unknown';
    return this.currentVersion;
  }

  /**
   * Запросить последнюю версию с сервера
   */
  async fetchLatestVersion(): Promise<VersionMeta | null> {
    try {
      // Добавляем cache-busting параметр
      const cacheBuster = Date.now();
      const response = await fetch(`/version.json?t=${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      if (!response.ok) {
        // В dev режиме version.json может отсутствовать - это нормально
        if (import.meta.env.DEV) {
          console.debug('[versionCheck] version.json не найден в dev режиме (это нормально)');
        } else {
          console.error('[versionCheck] Не удалось получить version.json', response.status);
        }
        return null;
      }

      const data = (await response.json()) as VersionMeta;
      return data;
    } catch (error) {
      // В dev режиме игнорируем ошибки парсинга JSON
      if (import.meta.env.DEV) {
        console.debug('[versionCheck] Проверка версий отключена в dev режиме');
      } else {
        console.error('[versionCheck] Ошибка при запросе version.json', error);
      }
      return null;
    }
  }

  /**
   * Сравнить версии
   * Возвращает true если latestVersion новее currentVersion
   */
  private compareVersions(currentVersion: string, latestVersion: string): boolean {
    // Если версии одинаковые
    if (currentVersion === latestVersion) {
      return false;
    }

    // Если текущая версия 'unknown', считаем что она устарела
    if (currentVersion === 'unknown') {
      return true;
    }

    // Если версии в формате ISO timestamp (как buildTime)
    try {
      const currentDate = new Date(currentVersion);
      const latestDate = new Date(latestVersion);

      if (!isNaN(currentDate.getTime()) && !isNaN(latestDate.getTime())) {
        return latestDate.getTime() > currentDate.getTime();
      }
    } catch (error) {
      // Не удалось распарсить как дату
    }

    // Семантическое версионирование (например, 2.8.6)
    const currentParts = currentVersion.split('.').map(Number);
    const latestParts = latestVersion.split('.').map(Number);

    for (let i = 0; i < Math.max(currentParts.length, latestParts.length); i++) {
      const current = currentParts[i] || 0;
      const latest = latestParts[i] || 0;

      if (latest > current) {
        return true;
      }
      if (latest < current) {
        return false;
      }
    }

    // Если не удалось определить, считаем что версии разные
    return currentVersion !== latestVersion;
  }

  /**
   * Выполнить проверку версии
   */
  async performCheck(): Promise<void> {
    // Если уже устарели, не выполняем проверку
    if (getVersionOutdated()) {
      return;
    }

    try {
      const currentVersion = this.getCurrentVersion();
      const latestMeta = await this.fetchLatestVersion();

      if (!latestMeta) {
        // Не удалось получить информацию о версии, пропускаем проверку
        return;
      }

      // Используем version если есть, иначе buildTime
      const latestVersion = latestMeta.version || latestMeta.buildTime;

      console.log('[versionCheck] Проверка версии:', {
        current: currentVersion,
        latest: latestVersion,
      });

      const isOutdated = this.compareVersions(currentVersion, latestVersion);

      if (isOutdated) {
        console.warn('[versionCheck] Обнаружена новая версия!', {
          current: currentVersion,
          latest: latestVersion,
        });

        // Остановить периодическую проверку
        this.stopPeriodicCheck();

        // Установить флаг устаревания
        setVersionOutdated(true);

        // Уведомить все вкладки
        notifyVersionOutdated(currentVersion, latestVersion);

        // Остановить все периодические задачи
        stopAllPeriodicTasks();

        // Вызвать callback если установлен
        if (this.config.onOutdated) {
          try {
            this.config.onOutdated(currentVersion, latestVersion);
          } catch (error) {
            console.error('[versionCheck] Ошибка в onOutdated callback', error);
          }
        }
      }
    } catch (error) {
      console.error('[versionCheck] Ошибка при проверке версии', error);
    }
  }

  /**
   * Запустить периодическую проверку версии
   */
  startPeriodicCheck(config?: VersionCheckConfig): void {
    // В dev режиме не запускаем проверку версий
    if (import.meta.env.DEV) {
      console.debug('[versionCheck] Проверка версий отключена в dev режиме');
      return;
    }

    if (this.checkTimer !== null) {
      console.warn('[versionCheck] Периодическая проверка уже запущена');
      return;
    }

    this.config = {
      checkInterval: DEFAULT_CHECK_INTERVAL,
      ...config,
    };

    console.log(
      `[versionCheck] Запуск периодической проверки версии каждые ${this.config.checkInterval! / 1000 / 60} минут`
    );

    // Выполнить первую проверку сразу
    void this.performCheck();

    // Запланировать последующие проверки
    this.checkTimer = window.setInterval(() => {
      void this.performCheck();
    }, this.config.checkInterval!);
  }

  /**
   * Остановить периодическую проверку версии
   */
  stopPeriodicCheck(): void {
    if (this.checkTimer !== null) {
      window.clearInterval(this.checkTimer);
      this.checkTimer = null;
      console.log('[versionCheck] Периодическая проверка версии остановлена');
    }
  }

  /**
   * Проверить, устарела ли версия
   */
  isOutdated(): boolean {
    return getVersionOutdated();
  }
}

export const versionCheckService = new VersionCheckService();
export default versionCheckService;
